import React, { useState, useMemo, useCallback } from 'react';
import { DotsVerticalIcon } from '@heroicons/react/outline';
import { Menu } from '@headlessui/react';
import CustomDateRangePicker from '@appmaker/components/DateRangePicker';
import { useCampaignInsights } from '../../hooks/useEngageApi';
import dayjs from 'dayjs';

// Memoized table headers
const tableHeaders = [
	'Campaign',
	'Status',
	'Delivered',
	'Clicked',
	'Opened',
	'Converted',
];

// Optimized empty state component
const EmptyState = React.memo(() => (
	<div className="flex flex-col items-center justify-center py-12 h-96">
		<h3 className="text-lg font-medium text-gray-900 mb-1">No data found</h3>
		<p className="text-gray-500">
			As soon as there is an activity, the information will appear here.
		</p>
	</div>
));

EmptyState.displayName = 'EmptyState';

// Optimized status badge component
const StatusBadge = React.memo(({ status }) => {
	const statusStyles = {
		Active: 'text-green-500 bg-green-100',
		ACTIVE: 'text-green-500 bg-green-100',
		Paused: 'text-yellow-700 bg-yellow-100',
		PAUSED: 'text-yellow-700 bg-yellow-100',
		'Setup Required': 'text-gray-700 bg-gray-100',
		Inactive: 'text-red-700 bg-red-100',
	};

	return (
		<span
			className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
				statusStyles[status] || 'text-gray-700 bg-gray-100'
			}`}
		>
			{status}
		</span>
	);
});

StatusBadge.displayName = 'StatusBadge';

// Optimized action menu component
const ActionMenu = React.memo(({ actions, campaignId, onActionClick }) => (
	<Menu as="div" className="relative inline-block text-left">
		<div>
			<Menu.Button className="inline-flex justify-center w-full px-2 py-2 bg-white text-gray-700 hover:bg-gray-50">
				<DotsVerticalIcon className="h-5 w-5" aria-hidden="true" />
			</Menu.Button>
		</div>

		<Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
			<div className="py-1">
				{actions.map((action) => (
					<Menu.Item key={action}>
						{({ active }) => (
							<button
								className={`block px-4 py-2 text-sm w-full text-left ${
									active
										? 'bg-gray-100 text-gray-900'
										: 'text-gray-700'
								}`}
								onClick={() => onActionClick(action, campaignId)}
							>
								{action}
							</button>
						)}
					</Menu.Item>
				))}
			</div>
		</Menu.Items>
	</Menu>
));

ActionMenu.displayName = 'ActionMenu';

// Optimized loading state component
const LoadingState = React.memo(() => (
	<div className="p-6">
		<div className="mb-6 flex flex-row justify-between items-center">
			<h2 className="text-2xl font-medium text-gray-900">Campaign Insights</h2>
		</div>
		<div className="flex items-center justify-center py-12 h-96">
			<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
		</div>
	</div>
));

LoadingState.displayName = 'LoadingState';

// Optimized error state component
const ErrorState = React.memo(() => (
	<div className="p-6">
		<div className="mb-6 flex flex-row justify-between items-center">
			<h2 className="text-2xl font-medium text-gray-900">Campaign Insights</h2>
		</div>
		<div className="flex flex-col items-center justify-center py-12 h-96">
			<h3 className="text-lg font-medium text-gray-900 mb-1">Error loading data</h3>
			<p className="text-gray-500">Please try again later.</p>
		</div>
	</div>
));

ErrorState.displayName = 'ErrorState';

const CampaignInsights = ({ projectId }) => {
	const [dateRange, setDateRange] = useState(() => {
		const today = new Date();
		const endDate = new Date(today);
		endDate.setDate(today.getDate() - 1); // Yesterday (not including today)
		
		const startDate = new Date(today);
		startDate.setDate(today.getDate() - 7); // 7 days ago
		
		return [startDate, endDate];
	});

	// Memoized date range parameters for API
	const queryParams = useMemo(() => ({
		from: dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : undefined,
		to: dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : undefined,
	}), [dateRange]);

	// Fetch campaign insights data
	const {
		data: insightsData,
		isLoading,
		error,
	} = useCampaignInsights(projectId, queryParams);

	// Helper function to convert slug to readable campaign name
	const formatCampaignName = (slug) => {
		const nameMap = {
			'WELCOME_USER': 'Welcome New Users',
			'ABANDONED_CART': 'Abandoned Cart',
			'REMOVED_FROM_CART': 'Removed from Cart',
			'ABANDONED_CHECKOUT': 'Abandoned Checkout',
			'INACTIVE_USER': 'Inactive User',
		};
		return nameMap[slug] || slug.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
	};

	// Helper function to format status
	const formatStatus = (status) => {
		if (!status) return 'Setup Required';
		return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
	};

	// Helper function to format numbers with commas
	const formatNumber = (num) => {
		if (num === 0 || num === null || num === undefined) return '0';
		return num.toLocaleString();
	};

	// Transform API data to match expected format
	const campaigns = useMemo(() => {
		if (!insightsData?.data) return [];
		
		// Transform API response to match component expectations
		return insightsData.data.map((item, index) => ({
			id: item.slug || index,
			slug: item.slug,
			Campaign: formatCampaignName(item.slug),
			Status: formatStatus(item.status),
			Delivered: item.delivered || 0,
			Clicked: item.clicked || 0,
			Opened: item.opened || 0,
			Converted: item.converted || 0,
			sequences: item.sequences || [],
			rawStatus: item.status, // Keep original status for logic
			actions: item.status === 'ACTIVE' 
				? ['Edit', 'Pause'] 
				: item.sequences && item.sequences.length > 0
					? ['Edit', 'Activate']
					: ['Setup'],
		}));
	}, [insightsData]);



	// Memoized event handlers
	const handleDateRangeChange = useCallback((newDateRange) => {
		setDateRange(newDateRange);
	}, []);

	const handleActionClick = useCallback((action, campaignId) => {
		if (process.env.NODE_ENV === 'development') {
			console.log(`${action} clicked for campaign ${campaignId}`);
		}
		
		// Find the campaign data
		const campaign = campaigns.find(c => c.id === campaignId);
		
		// Implement action handlers here
		switch (action) {
			case 'Edit':
			case 'Setup':
			case 'Activate':
			case 'Pause':
				// Navigate to campaigns page for editing
				// You can implement navigation here based on your routing setup
				// For example: navigate('/p-engage/campaigns')
				console.log(`Navigating to campaigns page for ${action} on ${campaign?.slug}`);
				break;
			default:
				break;
		}
	}, [campaigns]);

	// Show loading state
	if (isLoading) {
		return <LoadingState />;
	}

	// Show error state
	if (error) {
		return <ErrorState />;
	}



	return (
		<div className="p-6">
			<div className="mb-6 flex flex-row justify-between items-center">
				<h2 className="text-2xl font-medium text-gray-900">
					Campaign Insights
				</h2>
				<CustomDateRangePicker 
					value={dateRange} 
					onChange={handleDateRangeChange} 
				/>
			</div>
			<div className="bg-white border border-gray-200">
				{campaigns.length === 0 ? (
					<EmptyState />
				) : (
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-blue-600">
							<tr>
								{tableHeaders.map((header, index) => (
									<th
										key={index}
										className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider"
									>
										{header}
									</th>
								))}
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{campaigns.map((campaign) => (
								<tr key={campaign.id}>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
										{campaign.Campaign}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										<StatusBadge status={campaign.Status} />
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										{formatNumber(campaign.Delivered)}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										{formatNumber(campaign.Clicked)}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										{formatNumber(campaign.Opened)}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										{formatNumber(campaign.Converted)}
									</td>
									{/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
										<ActionMenu 
											actions={campaign.actions}
											campaignId={campaign.id}
											onActionClick={handleActionClick}
										/>
									</td> */}
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		</div>
	);
};

export default React.memo(CampaignInsights);
